/**
 * ContentForge — Data Controller
 *
 * Fetches data from the ContentForge RPC methods.
 */

import type { GatewayBrowserClient } from "../gateway.ts";

// ─── Types ──────────────────────────────────────────────────────────────

export type ContentForgeDashboardStats = {
  publishedToday: number;
  avgSeoScore: number;
  brokenLinks: number;
  queueSize: number;
  pendingApprovals: number;
  totalArticles: number;
  totalKeywords: number;
  totalProducts: number;
};

export type ContentForgeAgentState = {
  type: string;
  status: "idle" | "working" | "error" | "disabled";
  currentTask: string | null;
  currentKeywordId: string | null;
  lastActiveAt: number | null;
};

export type ContentForgePipelineCounts = {
  ideas: number;
  outline: number;
  sourcing: number;
  writing: number;
  review: number;
  scheduled: number;
  published: number;
};

export type ContentForgeActivityEntry = {
  id: string;
  timestamp: number;
  agentType: string;
  action: string;
  details: string;
  keywordId: string | null;
  status: "info" | "success" | "warning" | "error";
};

export type CfPinterestBoard = {
  id: string;
  name: string;
  pillar: string;
  created: boolean;
  published: number;
  pending: number;
  clicks: number;
  impressions: number;
  saves: number;
};

export type CfPinterestPin = {
  id: string;
  title: string;
  templateType: string;
  status: string;
  boardName: string;
  publishedAt: number | null;
  estimatedAt: number | null;
  clicks: number | null;
  impressions: number | null;
  saves: number | null;
  error: string | null;
  link: string;
};

export type CfPinStrategist = {
  measuredPins: number;
  publishedPins: number;
  totals: { impressions: number; clicks: number; saves: number; source?: "account" | "pins" };
  byTemplate: Array<{
    template: string;
    pins: number;
    impressions: number;
    clicks: number;
    saves: number;
  }>;
  lastReflectionAt: number | null;
  lastReflectionReport: string | null;
  nextReflectionAt: number | null;
  lastAnalyticsPullAt: number | null;
  minPinsForRetune: number;
  analyticsDaily: Array<{
    date: string;
    impressions: number;
    clicks: number;
    saves: number;
    measured: number;
  }>;
  accountDaily?: Array<{ date: string; impressions: number; clicks: number; saves: number }>;
  weightsHistory: Array<{
    at: number;
    before: Record<string, number>;
    after: Record<string, number>;
  }>;
  strategistLog: Array<{ at: number; kind: string; detail: string }>;
};

export type CfPinterestSite = {
  siteId: string;
  siteName: string;
  dailyPinCap: number;
  variantsPerArticle?: number;
  activeHours: [number, number];
  postedToday: number;
  pendingTotal: number;
  failedTotal: number;
  publishedTotal: number;
  templateWeights: Record<string, number> | null;
  tokenExpiresAt: number | null;
  lastAnalyticsPullAt: number | null;
  lastReflectionAt: number | null;
  boards: CfPinterestBoard[];
  recent: CfPinterestPin[];
  strategist?: CfPinStrategist;
};

export type ContentForgeDashboardData = {
  stats: ContentForgeDashboardStats;
  agentStates: ContentForgeAgentState[];
  recentActivity: ContentForgeActivityEntry[];
  pipelineCounts: ContentForgePipelineCounts;
  arthurPaused: boolean;
  sitePauseStates: Record<string, boolean>;
  pinterest?: CfPinterestSite[];
};

// ─── State ──────────────────────────────────────────────────────────────

export type ContentForgeState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  contentforgeLoading: boolean;
  contentforgeError: string | null;
  contentforgeDashboard: ContentForgeDashboardData | null;
  contentforgeSubView: ContentForgeSubView;
  // Tasks
  cfTasks: CfTask[];
  cfTasksLoading: boolean;
  cfTasksError: string | null;
  // Keywords
  cfKeywords: CfKeyword[];
  cfKeywordsLoading: boolean;
  cfKeywordsError: string | null;
};

export type ContentForgeSubView =
  | "dashboard"
  | "tasks"
  | "content"
  | "approvals"
  | "calendar"
  | "projects"
  | "memory"
  | "office"
  | "team"
  | "settings";

// ─── Task + Keyword types (mirroring backend) ──────────────────────────

export type CfTaskStatus = "recurring" | "backlog" | "in-progress" | "review" | "done";

export type CfAgentType =
  | "strategist"
  | "sourcer"
  | "writer"
  | "artist"
  | "publisher"
  | "auditor"
  | "miner"
  | "boss";

export type CfTask = {
  id: string;
  title: string;
  agentType: CfAgentType;
  keywordId: string | null;
  status: CfTaskStatus;
  input: Record<string, unknown> | null;
  output: Record<string, unknown> | null;
  error: string | null;
  startedAt: number | null;
  completedAt: number | null;
  createdAt: number;
  updatedAt: number;
};

export type CfKeywordStage =
  | "ideas"
  | "outline"
  | "sourcing"
  | "writing"
  | "review"
  | "scheduled"
  | "published";

export type CfKeyword = {
  id: string;
  text: string;
  siteId: string;
  stage: CfKeywordStage;
  articleType: string | null;
  priority: number;
  projectId: string | null;
  assignedAgent: string | null;
  createdAt: number;
  updatedAt: number;
  // Phase 13F: orchestration activity
  lastActivity: {
    action: string;
    details: string;
    status: string;
    timestamp: number;
  } | null;
  // Amazon integration
  amazonTagged?: boolean;
  amazonAsin?: string;
  amazonProductTitle?: string;
  // TintFinder reverse mode
  reverse?: boolean;
};

// ─── Loaders ────────────────────────────────────────────────────────────

export async function loadContentForgeDashboard(state: ContentForgeState) {
  if (!state.client || !state.connected) {
    return;
  }
  if (state.contentforgeLoading) {
    return;
  }

  state.contentforgeLoading = true;
  state.contentforgeError = null;
  try {
    const res = await state.client.request<ContentForgeDashboardData>("contentforge.dashboard", {});
    if (res) {
      state.contentforgeDashboard = res;
    }
  } catch (err) {
    state.contentforgeError = String(err);
  } finally {
    state.contentforgeLoading = false;
  }
}

// ── Tasks ──────────────────────────────────────────────────────────────

export async function loadCfTasks(state: ContentForgeState) {
  if (!state.client || !state.connected) {
    console.warn("[CF] loadCfTasks: not connected", {
      client: !!state.client,
      connected: state.connected,
    });
    state.cfTasksError = "Not connected to gateway";
    return;
  }
  state.cfTasksLoading = true;
  state.cfTasksError = null;
  try {
    const res = await state.client.request<{ tasks: CfTask[] }>("contentforge.tasks.list", {});
    console.log("[CF] loadCfTasks result:", res);
    state.cfTasks = res?.tasks ?? [];
  } catch (err) {
    console.error("[CF] loadCfTasks error:", err);
    state.cfTasksError = String(err);
  } finally {
    state.cfTasksLoading = false;
  }
}

export async function createCfTask(
  state: ContentForgeState,
  title: string,
  agentType: CfAgentType,
) {
  if (!state.client || !state.connected) {
    console.warn("[CF] createCfTask: not connected");
    state.cfTasksError = "Not connected to gateway";
    return;
  }
  state.cfTasksError = null;
  try {
    console.log("[CF] creating task:", { title, agentType });
    await state.client.request("contentforge.tasks.create", {
      title,
      agentType,
    });
    console.log("[CF] task created, reloading list");
    await loadCfTasks(state);
  } catch (err) {
    console.error("[CF] createCfTask error:", err);
    state.cfTasksError = String(err);
  }
}

// ── Keywords ──────────────────────────────────────────────────────────

export async function loadCfKeywords(state: ContentForgeState, siteId?: string) {
  if (!state.client || !state.connected) {
    console.warn("[CF] loadCfKeywords: not connected");
    state.cfKeywordsError = "Not connected to gateway";
    return;
  }
  state.cfKeywordsLoading = true;
  state.cfKeywordsError = null;
  try {
    const params: Record<string, unknown> = {};
    if (siteId) {
      params.siteId = siteId;
    }
    const res = await state.client.request<{ keywords: CfKeyword[] }>(
      "contentforge.keywords.list",
      params,
    );
    console.log("[CF] loadCfKeywords result:", res);
    state.cfKeywords = res?.keywords ?? [];
  } catch (err) {
    console.error("[CF] loadCfKeywords error:", err);
    state.cfKeywordsError = String(err);
  } finally {
    state.cfKeywordsLoading = false;
  }
}

export async function addCfKeywords(state: ContentForgeState, siteId: string, texts: string[]) {
  if (!state.client || !state.connected) {
    console.warn("[CF] addCfKeywords: not connected");
    state.cfKeywordsError = "Not connected to gateway";
    return;
  }
  state.cfKeywordsError = null;
  try {
    console.log("[CF] adding keywords:", { siteId, texts });
    await state.client.request("contentforge.keywords.add", {
      siteId,
      texts,
    });
    console.log("[CF] keywords added, reloading list");
    // Reload without siteId filter so ALL keywords show
    await loadCfKeywords(state);
  } catch (err) {
    console.error("[CF] addCfKeywords error:", err);
    state.cfKeywordsError = String(err);
  }
}

export async function removeCfKeyword(state: ContentForgeState, id: string) {
  if (!state.client || !state.connected) {
    state.cfKeywordsError = "Not connected to gateway";
    return;
  }
  try {
    await state.client.request("contentforge.keywords.remove", { id });
    await loadCfKeywords(state);
  } catch (err) {
    console.error("[CF] removeCfKeyword error:", err);
    state.cfKeywordsError = String(err);
  }
}

export async function updateCfKeywordStage(
  state: ContentForgeState,
  id: string,
  stage: CfKeywordStage,
) {
  if (!state.client || !state.connected) {
    state.cfKeywordsError = "Not connected to gateway";
    return;
  }
  try {
    await state.client.request("contentforge.keywords.updateStage", {
      id,
      stage,
    });
    await loadCfKeywords(state);
  } catch (err) {
    console.error("[CF] updateCfKeywordStage error:", err);
    state.cfKeywordsError = String(err);
  }
}

export async function addReverseKeywords(
  state: ContentForgeState,
  siteId: string,
  texts: string[],
  productUrls: string[],
) {
  if (!state.client || !state.connected) {
    console.warn("[CF] addReverseKeywords: not connected");
    state.cfKeywordsError = "Not connected to gateway";
    return;
  }
  state.cfKeywordsError = null;
  try {
    console.log("[CF] adding reverse keywords:", { siteId, texts, productUrls });
    await state.client.request("contentforge.keywords.addReverse", {
      siteId,
      texts,
      productUrls,
    });
    console.log("[CF] reverse keywords added, reloading list");
    await loadCfKeywords(state);
  } catch (err) {
    console.error("[CF] addReverseKeywords error:", err);
    state.cfKeywordsError = String(err);
  }
}
